import React, { useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  User,
  Trophy,
  Calendar,
  Target,
  TrendingUp,
  Mail,
  Phone,
  Edit2,
  Loader2,
  Camera,
  Trash2,
  Upload,
  AlertCircle,
  Save,
  X
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatCurrency, getEloRank, generateInitials } from '../lib/utils';

export default function Profile() {
  const { user, updateUser, logout } = useAuth();
  const [editing, setEditing] = React.useState(false);
  const [editingProfile, setEditingProfile] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [name, setName] = React.useState(user?.name || '');
  const [phone, setPhone] = React.useState(user?.phone || '');
  const [birthdate, setBirthdate] = React.useState(user?.birthdate || '');
  const [gender, setGender] = React.useState(user?.gender || '');
  const fileInputRef = useRef(null);

  const eloRank = getEloRank(user?.elo_points || 1000);
  const winRate = user?.matches_played > 0 
    ? Math.round((user.wins / user.matches_played) * 100) 
    : 0;

  // Check if profile is incomplete
  const isProfileIncomplete = !user?.phone || !user?.birthdate || !user?.gender;

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Name darf nicht leer sein');
      return;
    }

    setLoading(true);
    try {
      const response = await userAPI.updateProfile({ name: name.trim() });
      updateUser(response.data);
      toast.success('Profil aktualisiert!');
      setEditing(false);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!phone.trim()) {
      toast.error('Telefonnummer ist erforderlich');
      return;
    }
    if (!birthdate) {
      toast.error('Geburtsdatum ist erforderlich');
      return;
    }
    if (!gender) {
      toast.error('Geschlecht ist erforderlich');
      return;
    }

    setLoading(true);
    try {
      const response = await userAPI.updateProfile({ 
        phone: phone.trim(),
        birthdate,
        gender
      });
      updateUser(response.data);
      toast.success('Profildaten aktualisiert!');
      setEditingProfile(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wähle ein Bild aus');
      return;
    }

    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error('Bild zu groß (max 500KB)');
      return;
    }

    setUploadingImage(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result;
        try {
          const response = await userAPI.uploadAvatar({ avatar_url: base64 });
          updateUser(response.data.user);
          toast.success('Profilbild aktualisiert!');
        } catch (error) {
          toast.error('Fehler beim Hochladen');
        } finally {
          setUploadingImage(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Fehler beim Verarbeiten des Bildes');
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    setUploadingImage(true);
    try {
      const response = await userAPI.deleteAvatar();
      updateUser(response.data.user);
      toast.success('Profilbild entfernt');
    } catch (error) {
      toast.error('Fehler beim Entfernen');
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24" data-testid="profile-page">
        {/* Profile Header */}
        <Card className="border-slate-200 mb-4 sm:mb-6 overflow-hidden" data-testid="profile-card">
          {/* Mobile/Tablet: Full gradient card with avatar and name */}
          <div className="lg:hidden">
            <div className="bg-gradient-to-br from-tunis-blue to-tunis-teal px-4 py-6 text-center rounded-t-lg">
              {/* Avatar */}
              <div className="relative group inline-block mb-4">
                <Avatar className="w-24 h-24 border-4 border-white shadow-lg mx-auto">
                  <AvatarImage src={user?.avatar_url} />
                  <AvatarFallback className="bg-white text-tunis-blue text-2xl font-bold">
                    {generateInitials(user?.name || 'U')}
                  </AvatarFallback>
                </Avatar>
                
                {/* Upload Overlay */}
                <div 
                  className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  onClick={handleImageSelect}
                >
                  {uploadingImage ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                </div>
                
                {/* Remove Button */}
                {user?.avatar_url && (
                  <button
                    onClick={handleRemoveImage}
                    className="absolute -bottom-1 -right-1 w-7 h-7 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors"
                    title="Bild entfernen"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-white" />
                  </button>
                )}
              </div>
              
              {/* Name - White text */}
              {editing ? (
                <div className="bg-white rounded-lg p-3 mx-2">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-10 text-center mb-2"
                    data-testid="input-name"
                  />
                  <div className="flex gap-2 justify-center">
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={loading}
                      className="bg-tunis-teal hover:bg-tunis-teal/90"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditing(false);
                        setName(user?.name || '');
                      }}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-1">
                    <h1 className="font-display font-bold text-2xl text-white">{user?.name}</h1>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditing(true)}
                      data-testid="edit-name-btn"
                      className="h-8 w-8 text-white hover:bg-white/20"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <Badge className="bg-white/20 text-white border border-white/40 text-xs px-3">
                      {eloRank.name}
                    </Badge>
                    <Badge className="bg-white/20 text-white border border-white/40 capitalize text-xs px-3">
                      {user?.role === 'field_owner' ? 'Feldbesitzer' : user?.role === 'admin' ? 'Admin' : 'Spieler'}
                    </Badge>
                  </div>
                </>
              )}
              
              {/* Upload Hint */}
              <p className="text-[10px] text-white/70 mt-4">
                <Camera className="w-3 h-3 inline mr-1" />
                Tippe auf dein Profilbild um es zu ändern
              </p>
            </div>
          </div>

          {/* Desktop Layout (lg and above) */}
          <div className="hidden lg:block">
            <div className="h-24 bg-gradient-to-r from-tunis-blue to-tunis-teal rounded-t-lg" />
            <CardContent className="px-6 pb-6">
              <div className="flex flex-row items-end gap-4 -mt-14">
                {/* Avatar */}
                <div className="relative group flex-shrink-0">
                  <Avatar className="w-28 h-28 border-4 border-white shadow-lg">
                    <AvatarImage src={user?.avatar_url} />
                    <AvatarFallback className="bg-tunis-blue text-white text-3xl font-bold">
                      {generateInitials(user?.name || 'U')}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div 
                    className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                    onClick={handleImageSelect}
                  >
                    {uploadingImage ? (
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6 text-white" />
                    )}
                  </div>
                  
                  {user?.avatar_url && (
                    <button
                      onClick={handleRemoveImage}
                      className="absolute -bottom-1 -right-1 w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors"
                      title="Bild entfernen"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  )}
                </div>
                
                {/* Name and Info - Desktop */}
                <div className="flex-1 pb-2">
                  {editing ? (
                    <div className="flex flex-row gap-2">
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-10 max-w-xs"
                        data-testid="input-name"
                      />
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={loading}
                        className="bg-tunis-teal hover:bg-tunis-teal/90"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditing(false);
                          setName(user?.name || '');
                        }}
                      >
                        Abbrechen
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h1 className="font-display font-bold text-2xl text-tunis-blue">{user?.name}</h1>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditing(true)}
                        data-testid="edit-name-btn"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`${eloRank.bg} ${eloRank.color} text-xs`}>
                      {eloRank.name}
                    </Badge>
                    <Badge variant="outline" className="capitalize text-xs">
                      {user?.role === 'field_owner' ? 'Feldbesitzer' : user?.role === 'admin' ? 'Admin' : 'Spieler'}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    <Camera className="w-3 h-3 inline mr-1" />
                    Klicke auf dein Profilbild um es zu ändern (max 500KB)
                  </p>
                </div>
              </div>
            </CardContent>
          </div>

          {/* Hidden File Input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageChange}
            accept="image/*"
            className="hidden"
          />
        </Card>

        {/* Profile Incomplete Warning */}
        {isProfileIncomplete && (
          <Card className="mb-4 sm:mb-6 border-amber-300 bg-amber-50">
            <CardContent className="pt-4 pb-4 px-3 sm:px-4">
              <div className="flex flex-col sm:flex-row items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0 hidden sm:block" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 sm:gap-0">
                    <AlertCircle className="w-5 h-5 text-amber-600 sm:hidden" />
                    <h4 className="font-semibold text-amber-800 text-sm sm:text-base">Profil unvollständig</h4>
                  </div>
                  <p className="text-xs sm:text-sm text-amber-700 mt-1">
                    Bitte vervollständige deine Profildaten (Telefonnummer, Geburtsdatum, Geschlecht), um alle Funktionen nutzen zu können.
                  </p>
                  <Button
                    size="sm"
                    className="mt-3 bg-amber-600 hover:bg-amber-700 w-full sm:w-auto text-xs sm:text-sm"
                    onClick={() => setEditingProfile(true)}
                  >
                    <Edit2 className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                    Jetzt vervollständigen
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Card>
            <CardContent className="pt-3 pb-3 sm:pt-4 sm:pb-4 text-center">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-tunis-teal mx-auto mb-1" />
              <div className="font-bold text-lg sm:text-2xl text-tunis-blue">{user?.elo_points || 1000}</div>
              <div className="text-[10px] sm:text-xs text-muted-foreground">ELO Punkte</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-3 pb-3 sm:pt-4 sm:pb-4 text-center">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-tunis-teal mx-auto mb-1" />
              <div className="font-bold text-lg sm:text-2xl text-tunis-blue">{user?.matches_played || 0}</div>
              <div className="text-[10px] sm:text-xs text-muted-foreground">Spiele</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-3 pb-3 sm:pt-4 sm:pb-4 text-center">
              <Target className="w-5 h-5 sm:w-6 sm:h-6 text-tunis-teal mx-auto mb-1" />
              <div className="font-bold text-lg sm:text-2xl text-tunis-blue">{user?.wins || 0}</div>
              <div className="text-[10px] sm:text-xs text-muted-foreground">Siege</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-3 pb-3 sm:pt-4 sm:pb-4 text-center">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-tunis-teal mx-auto mb-1" />
              <div className="font-bold text-lg sm:text-2xl text-tunis-blue">{winRate}%</div>
              <div className="text-[10px] sm:text-xs text-muted-foreground">Siegrate</div>
            </CardContent>
          </Card>
        </div>

        {/* Personal Info - Edit Mode */}
        {editingProfile ? (
          <Card className="mb-6 border-tunis-teal">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-tunis-teal" />
                Persönliche Daten bearbeiten
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Telefonnummer *</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+216 XX XXX XXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="birthdate">Geburtsdatum *</Label>
                <Input
                  id="birthdate"
                  type="date"
                  value={birthdate}
                  onChange={(e) => setBirthdate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="gender">Geschlecht *</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger>
                    <SelectValue placeholder="Bitte auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Männlich</SelectItem>
                    <SelectItem value="female">Weiblich</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSaveProfile}
                  disabled={loading}
                  className="bg-tunis-teal hover:bg-tunis-teal/90"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Speichern
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingProfile(false);
                    setPhone(user?.phone || '');
                    setBirthdate(user?.birthdate || '');
                    setGender(user?.gender || '');
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Abbrechen
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Contact Info - View Mode */
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-tunis-teal" />
                Persönliche Daten
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setEditingProfile(true)}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <div className="text-sm text-muted-foreground">E-Mail</div>
                  <div className="font-medium">{user?.email}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <div className="text-sm text-muted-foreground">Telefon</div>
                  <div className="font-medium">
                    {user?.phone || <span className="text-amber-600">Nicht angegeben</span>}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <div className="text-sm text-muted-foreground">Geburtsdatum</div>
                  <div className="font-medium">
                    {user?.birthdate ? new Date(user.birthdate).toLocaleDateString('de-DE') : <span className="text-amber-600">Nicht angegeben</span>}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-slate-400" />
                <div>
                  <div className="text-sm text-muted-foreground">Geschlecht</div>
                  <div className="font-medium">
                    {user?.gender === 'male' ? 'Männlich' : user?.gender === 'female' ? 'Weiblich' : <span className="text-amber-600">Nicht angegeben</span>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Wallet Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="w-5 h-5 text-tunis-teal" />
              Wallet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Guthaben</span>
              <span className="font-bold text-xl text-tunis-teal">
                {formatCurrency(user?.wallet_balance || 0)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
